import React, { useState, useEffect } from 'react';
import './AdminPage.css';
import { useTournaments } from '../contexts/TournamentContext';
import { useAuth } from '../contexts/AuthContext';
import { apiUrl } from '../config/api';

const AdminPage = () => {
  const { session } = useAuth();
  const [botStatus, setBotStatus] = useState(null);
  const [logs, setLogs] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('status');
  const [isAdmin, setIsAdmin] = useState(null); // null = checking, false = not admin, true = admin

  // Tournament State
  const { 
    listTournaments, 
    createTournament, 
    startTournament, 
    inviteUserToTournament,
    updateTournament,
    cancelTournament,
    deleteTournament,
    tournaments 
  } = useTournaments();
  
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [selectedTournament, setSelectedTournament] = useState(null);
  const [inviteSearchQuery, setInviteSearchQuery] = useState('');
  const [inviteSearchResults, setInviteSearchResults] = useState([]);
  
  // Invite Codes State
  const [showInviteCodesModal, setShowInviteCodesModal] = useState(false);
  const [inviteCodes, setInviteCodes] = useState([]);
  const [inviteCodeCount, setInviteCodeCount] = useState(10);
  
  // Edit Tournament DTO
  const [editTournamentData, setEditTournamentData] = useState({
      name: '',
      description: '',
      max_players: 0,
      start_time: '',
      end_time: '',
      registration_start: '',
      registration_end: '',
      is_money_prize: false,
      prize_pool: {
        currency: 'USD',
        total_amount: 0,
        entry_fee: 0,
        distribution: '50,30,20'
      }
  });
  
  // New Tournament DTO
  const [newTournament, setNewTournament] = useState({
    name: '',
    description: '',
    type: 'bracket',
    access_type: 'public',
    max_players: 16,
    min_players: 4,
    time_control: { minutes: 10, increment: 0 },
    start_time: '',
    end_time: '',
    registration_start: '',
    registration_end: '',
    is_money_prize: false,
    prize_pool: {
      currency: 'USD',
      total_amount: 0, 
      entry_fee: 0,
      distribution: '50,30,20'
    }
  });

  // Check if user has admin access
  const checkAdminAccess = async () => {
    try {
      const token = session?.access_token;
      if (!token) {
          setIsAdmin(false);
          return;
      }

      const response = await fetch(apiUrl('admin/check'), {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setIsAdmin(data.is_admin || false);
      } else {
        setIsAdmin(false);
      }
    } catch (err) {
      console.error("Admin check failed:", err);
      setIsAdmin(false);
    }
  };

  // Fetch bot status
  const fetchBotStatus = async () => {
    try {
      setLoading(true);
      const token = session?.access_token;
      const response = await fetch(apiUrl('admin/bots/status'), {
        headers: {
            'Authorization': `Bearer ${token}`
        }
      });
      const data = await response.json();
      if (response.ok) {
        setBotStatus(data.status);
      } else {
        setError(data.error || 'Failed to fetch bot status');
      }
    } catch (err) {
      setError('Failed to connect to server');
    } finally {
      setLoading(false);
    }
  };

  // Fetch bot logs
  const fetchBotLogs = async () => {
    try {
      setLoading(true);
      const token = session?.access_token;
      const response = await fetch(apiUrl('admin/bots/logs?lines=200'), {
        headers: {
            'Authorization': `Bearer ${token}`
        }
      });
      const data = await response.json();
      if (response.ok) {
        setLogs(data.logs);
      } else {
        setError(data.error || 'Failed to fetch bot logs');
      }
    } catch (err) {
      setError('Failed to connect to server');
    } finally {
      setLoading(false);
    }
  };

  // Fetch tournament invite codes
  const fetchInviteCodes = async (tournamentId) => {
    try {
      setLoading(true);
      const token = session?.access_token;
      // MATCH BACKEND: GET /admin/tournaments/:id/invite-codes
      const response = await fetch(apiUrl(`admin/tournaments/${tournamentId}/invite-codes`), {
         headers: {
            'Authorization': `Bearer ${token}`
         }
      });
      const data = await response.json();
      if (response.ok) {
        setInviteCodes(data.codes || []);
      } else {
        setError(data.error || 'Failed to fetch invite codes');
      }
    } catch (err) {
      setError('Failed to fetch invite codes: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const generateInviteCodes = async () => {
      if (!selectedTournament) return;
      try {
        setLoading(true);
        const token = session?.access_token;
        // MATCH BACKEND: POST /admin/tournaments/:id/invite-codes
        const response = await fetch(apiUrl(`admin/tournaments/${selectedTournament.id}/invite-codes`), {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({ count: parseInt(inviteCodeCount) })
        });
        
        const data = await response.json();
        if (response.ok) {
            // refresh codes
            fetchInviteCodes(selectedTournament.id);
        } else {
            setError(data.error || 'Failed to generate codes');
        }
      } catch (err) {
        setError('Failed to generate codes');
      } finally {
        setLoading(false);
      }
  };

  const exportInviteCodes = () => {
      if (!inviteCodes.length) return;
      
      const csvContent = "data:text/csv;charset=utf-8," 
          + "Code,Is Used,Used By,Created At\n"
          + inviteCodes.map(row => `${row.code},${row.is_used},${row.used_by_user_id || ''},${row.created_at}`).join("\n");
          
      const encodedUri = encodeURI(csvContent);
      const link = document.createElement("a");
      link.setAttribute("href", encodedUri);
      link.setAttribute("download", `tournament_${selectedTournament?.id || 'codes'}_invites.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
  };

  // Restart bot system
  const restartBots = async () => {
    if (!window.confirm('Are you sure you want to restart the bot system?')) {
      return;
    }

    try {
      setLoading(true);
      const token = session?.access_token;
      const response = await fetch(apiUrl('admin/bots/restart'), {
        method: 'POST',
        headers: {
            'Authorization': `Bearer ${token}`
        }
      });
      const data = await response.json();
      if (response.ok) {
        alert('Bot system restart initiated successfully');
        // Refresh status after a delay
        setTimeout(fetchBotStatus, 3000);
      } else {
        setError(data.error || 'Failed to restart bot system');
      }
    } catch (err) {
      setError('Failed to connect to server');
    } finally {
      setLoading(false);
    }
  };

  // Handle Tournament Actions
  const handleCreateTournament = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      // Clean up data for submission
      const submissionData = {
        ...newTournament,
        start_time: newTournament.start_time ? new Date(newTournament.start_time).toISOString() : null,
        end_time: newTournament.end_time ? new Date(newTournament.end_time).toISOString() : null,
        registration_start: newTournament.registration_start ? new Date(newTournament.registration_start).toISOString() : null,
        registration_end: newTournament.registration_end ? new Date(newTournament.registration_end).toISOString() : null,
      };

      if (newTournament.is_money_prize) {
        submissionData.prize_pool.distribution = newTournament.prize_pool.distribution.split(',').map(s => parseFloat(s.trim()));
      } else {
         delete submissionData.prize_pool;
      }

      await createTournament(submissionData);
      setShowCreateModal(false);
      // Reset form
      setNewTournament({
         name: '',
         description: '',
         type: 'swiss',
         access_type: 'public',
         max_players: 16,
         min_players: 4,
         time_control: { minutes: 10, increment: 0 },
         start_time: '',
         end_time: '',
         registration_start: '',
         registration_end: '',
         is_money_prize: false,
         prize_pool: { currency: 'USD', total_amount: 0, entry_fee: 0, distribution: '50,30,20' }
      });
    } catch(err) {
        setError(err.message);
    } finally {
        setLoading(false);
    }
  };

  const handleStartTournament = async (id) => {
      if(!window.confirm('Are you sure you want to manually start this tournament?')) return;
      try {
          await startTournament(id);
      } catch(err) {
          setError(err.message);
      }
  };

  const handleSearchUsers = async () => {
      if (!inviteSearchQuery) return;
      try {
          const token = session?.access_token;
          const res = await fetch(apiUrl(`users/search?q=${inviteSearchQuery}`), {
               headers: { 'Authorization': `Bearer ${token}` }
          });
          const data = await res.json();
          setInviteSearchResults(data.users || []);
      } catch(err) {
          console.error(err);
      }
  };

  const handleInviteUser = async (userId) => {
      try {
          await inviteUserToTournament(selectedTournament.id, userId);
          alert('Invitation sent!');
      } catch(err) {
          setError(err.message);
      }
  };

  const handleEditTournament = (tournament) => {
      setSelectedTournament(tournament);
      setEditTournamentData({
          name: tournament.name,
          description: tournament.description || '',
          max_players: tournament.max_players,
          start_time: tournament.start_time ? new Date(tournament.start_time).toISOString().slice(0, 16) : '',
          end_time: tournament.end_time ? new Date(tournament.end_time).toISOString().slice(0, 16) : '',
          registration_start: tournament.registration_start ? new Date(tournament.registration_start).toISOString().slice(0, 16) : '',
          registration_end: tournament.registration_end ? new Date(tournament.registration_end).toISOString().slice(0, 16) : '',
          is_money_prize: tournament.is_money_prize || false,
          prize_pool: {
            currency: tournament.prize_pool?.currency || 'USD',
            total_amount: tournament.prize_pool?.total_amount || 0,
            entry_fee: tournament.prize_pool?.entry_fee || 0,
            distribution: tournament.prize_pool?.distribution ? tournament.prize_pool.distribution.join(',') : '50,30,20'
          }
      });
      setShowEditModal(true);
  };

  const handleUpdateTournament = async (e) => {
      e.preventDefault();
      try {
          setLoading(true);
          const updates = {
              ...editTournamentData,
              start_time: editTournamentData.start_time ? new Date(editTournamentData.start_time).toISOString() : null,
              end_time: editTournamentData.end_time ? new Date(editTournamentData.end_time).toISOString() : null,
              registration_start: editTournamentData.registration_start ? new Date(editTournamentData.registration_start).toISOString() : null,
              registration_end: editTournamentData.registration_end ? new Date(editTournamentData.registration_end).toISOString() : null
          };

          if (editTournamentData.is_money_prize) {
             updates.prize_pool.distribution = editTournamentData.prize_pool.distribution.split(',').map(s => parseFloat(s.trim()));
          } else {
             delete updates.prize_pool;
          }

          await updateTournament(selectedTournament.id, updates);
          setShowEditModal(false);
          setSelectedTournament(null);
      } catch(err) {
          setError(err.message);
      } finally {
          setLoading(false);
      }
  };

  const handleCancelAction = async (id) => {
      if(!window.confirm('Are you sure you want to CANCEL this tournament? This cannot be undone.')) return;
      try {
          setLoading(true);
          await cancelTournament(id);
      } catch(err) {
          setError(err.message);
      } finally {
          setLoading(false);
      }
  };

  const handleDeleteAction = async (id) => {
      if(!window.confirm('Are you sure you want to DELETE this tournament? ALL data will be lost.')) return;
      try {
          setLoading(true);
          await deleteTournament(id);
      } catch(err) {
          setError(err.message);
      } finally {
          setLoading(false);
      }
  };

  const handleToggleInviteCodeSent = async (codeId, currentStatus) => {
    try {
      setLoading(true);
      const response = await fetch(apiUrl(`admin/invite-codes/${codeId}/toggle-sent`), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to toggle invite code sent status');
      }

      // Refresh invite codes list
      await fetchInviteCodes();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Check admin access on component mount and when session changes
  useEffect(() => {
    if (session) {
        checkAdminAccess();
    }
  }, [session]);

  // Auto-refresh status every 30 seconds (only if admin)
  useEffect(() => {
    if (isAdmin === true) {
      fetchBotStatus();
      const interval = setInterval(fetchBotStatus, 30000);
      return () => clearInterval(interval);
    }
  }, [isAdmin]);

  // Fetch logs when logs tab is selected
  useEffect(() => {
    if (activeTab === 'logs') {
      fetchBotLogs();
    } else if (activeTab === 'tournaments') {
        listTournaments();
    }
  }, [activeTab, listTournaments]);

  const getStatusColor = (status) => {
    switch (status) {
      case 'running': return 'green';
      case 'stopped': return 'red';
      case 'error': return 'orange';
      default: return 'gray';
    }
  };

  const getMachineStateColor = (state) => {
    switch (state) {
      case 'started': return 'green';
      case 'stopped': return 'red';
      default: return 'orange';
    }
  };

  // Show loading while checking admin status
  if (isAdmin === null) {
    return (
      <div className="admin-page">
        <div className="loading">Checking access permissions...</div>
      </div>
    );
  }

  // Show access denied if not admin
  if (isAdmin === false) {
    return (
      <div className="admin-page">
        <div className="admin-header">
          <h1>🔒 Access Denied</h1>
          <p>You do not have permission to access the admin panel.</p>
        </div>
        <div className="admin-content">
          <div className="error-banner">
            <span>⚠️ Admin access required</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-page">
      <div className="admin-header">
        <h1>🎭 Bot System Admin</h1>
        <p>Monitor and manage the RiftChess bot system</p>
      </div>

      {error && (
        <div className="error-banner">
          <span>⚠️ {error}</span>
          <button onClick={() => setError(null)}>×</button>
        </div>
      )}

      <div className="admin-tabs">
        <button
          className={activeTab === 'status' ? 'tab active' : 'tab'}
          onClick={() => setActiveTab('status')}
        >
          📊 Status
        </button>
        <button
          className={activeTab === 'logs' ? 'tab active' : 'tab'}
          onClick={() => setActiveTab('logs')}
        >
          📋 Logs
        </button>
        <button
          className={activeTab === 'actions' ? 'tab active' : 'tab'}
          onClick={() => setActiveTab('actions')}
        >
          🔧 Actions
        </button>
        <button
          className={activeTab === 'tournaments' ? 'tab active' : 'tab'}
          onClick={() => setActiveTab('tournaments')}
        >
          🏆 Tournaments
        </button>
      </div>

      <div className="admin-content">
        {activeTab === 'status' && (
          <div className="status-panel">
            <div className="status-header">
              <h2>Bot System Status</h2>
              <button onClick={fetchBotStatus} disabled={loading} className="refresh-btn">
                {loading ? '🔄' : '🔄'} Refresh
              </button>
            </div>

            {botStatus ? (
              <div className="status-info">
                <div className="status-card">
                  <h3>Overall Status</h3>
                  <div className="status-indicator">
                    <span
                      className="status-dot"
                      style={{backgroundColor: getStatusColor(botStatus.status)}}
                    ></span>
                    <span className="status-text">{botStatus.status.toUpperCase()}</span>
                  </div>
                  <p>App: {botStatus.app_name}</p>
                  <p>Last Updated: {new Date(botStatus.last_update).toLocaleString()}</p>
                </div>

                <div className="machines-section">
                  <h3>Bot Machines</h3>
                  {botStatus.machines && botStatus.machines.length > 0 ? (
                    <div className="machines-grid">
                      {botStatus.machines.map((machine, index) => (
                        <div key={machine.id || index} className="machine-card">
                          <div className="machine-header">
                            <span className="machine-id">🖥️ {machine.id}</span>
                            <span
                              className="machine-state"
                              style={{color: getMachineStateColor(machine.state)}}
                            >
                              {machine.state}
                            </span>
                          </div>
                          <p>Region: {machine.region}</p>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="no-machines">No machines found</p>
                  )}
                </div>
              </div>
            ) : loading ? (
              <div className="loading">Loading status...</div>
            ) : (
              <div className="no-data">Click refresh to load status</div>
            )}
          </div>
        )}

        {activeTab === 'logs' && (
          <div className="logs-panel">
            <div className="logs-header">
              <h2>Recent Logs</h2>
              <button onClick={fetchBotLogs} disabled={loading} className="refresh-btn">
                {loading ? '🔄' : '🔄'} Refresh
              </button>
            </div>

            <div className="logs-container">
              {logs ? (
                <pre className="logs-content">{logs}</pre>
              ) : loading ? (
                <div className="loading">Loading logs...</div>
              ) : (
                <div className="no-data">Click refresh to load logs</div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'actions' && (
          <div className="actions-panel">
            <h2>Bot System Actions</h2>

            <div className="action-card">
              <h3>🔄 Restart Bot System</h3>
              <p>Restart all bot machines. This will temporarily stop the bots and start them again.</p>
              <button
                onClick={restartBots}
                disabled={loading}
                className="restart-btn"
              >
                {loading ? 'Restarting...' : 'Restart Bots'}
              </button>
            </div>

            <div className="action-card">
              <h3>📊 System Info</h3>
              <p>The bot system runs 3 concurrent persona bots that automatically play games.</p>
              <ul>
                <li>Deployed on Fly.io (riftchess-bots.fly.dev)</li>
                <li>Uses Stockfish chess engine</li>
                <li>20 different personas with varying skills</li>
                <li>Automatic reconnection on failures</li>
              </ul>
            </div>
          </div>
        )}

        {activeTab === 'tournaments' && (
            <div className="tournaments-panel">
                <div className="panel-header" style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'20px'}}>
                    <h2>Tournament Management</h2>
                    <button onClick={() => setShowCreateModal(true)} className="create-btn" style={{padding:'8px 16px', backgroundColor:'#4CAF50', color:'white', border:'none', borderRadius:'4px', cursor:'pointer'}}>
                        + Create Tournament
                    </button>
                </div>

                <div className="tournaments-list">
                    <table style={{width:'100%', borderCollapse:'collapse'}}>
                        <thead>
                            <tr style={{textAlign:'left', borderBottom:'1px solid #444'}}>
                                <th style={{padding:'10px'}}>Name</th>
                                <th style={{padding:'10px'}}>Type</th>
                                <th style={{padding:'10px'}}>Access</th>
                                <th style={{padding:'10px'}}>Players</th>
                                <th style={{padding:'10px'}}>Status</th>
                                <th style={{padding:'10px'}}>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {tournaments.map(t => (
                                <tr key={t.id} style={{borderBottom:'1px solid #333'}}>
                                    <td style={{padding:'10px'}}>{t.name}</td>
                                    <td style={{padding:'10px'}}>{t.type}</td>
                                    <td style={{padding:'10px'}}>{t.access_type}</td>
                                    <td style={{padding:'10px'}}>{t.player_count}/{t.max_players}</td>
                                    <td style={{padding:'10px'}}>{t.status}</td>
                                    <td style={{padding:'10px'}}>
                                        {t.status === 'registering' && (
                                            <button onClick={() => handleStartTournament(t.id)} style={{marginRight:'5px', cursor:'pointer', backgroundColor:'#4CAF50', color:'white', border:'none', padding:'4px 8px', borderRadius:'4px'}}>Start</button>
                                        )}
                                        <button onClick={() => handleEditTournament(t)} style={{marginRight:'5px', cursor:'pointer', backgroundColor:'#FF9800', color:'white', border:'none', padding:'4px 8px', borderRadius:'4px'}}>Edit</button>
                                        <button onClick={() => { setSelectedTournament(t); setShowInviteModal(true); }} style={{marginRight:'5px', cursor:'pointer', backgroundColor:'#2196F3', color:'white', border:'none', padding:'4px 8px', borderRadius:'4px'}}>Invite</button>
                                        {t.access_type === 'invite_only' && (
                                            <button onClick={() => { setSelectedTournament(t); fetchInviteCodes(t.id); setShowInviteCodesModal(true); }} style={{marginRight:'5px', cursor:'pointer', backgroundColor:'#9C27B0', color:'white', border:'none', padding:'4px 8px', borderRadius:'4px'}}>Codes</button>
                                        )}
                                        {(t.status === 'registering' || t.status === 'active') && (
                                            <button onClick={() => handleCancelAction(t.id)} style={{marginRight:'5px', cursor:'pointer', backgroundColor:'#f44336', color:'white', border:'none', padding:'4px 8px', borderRadius:'4px'}}>Cancel</button>
                                        )}
                                        <button onClick={() => handleDeleteAction(t.id)} style={{cursor:'pointer', backgroundColor:'#9E9E9E', color:'white', border:'none', padding:'4px 8px', borderRadius:'4px'}}>Delete</button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        )}
      </div>



        {/* Invite Codes Modal */}
        {showInviteCodesModal && selectedTournament && (
            <div className="modal-overlay" style={{position:'fixed', top:0, left:0, right:0, bottom:0, backgroundColor:'rgba(0,0,0,0.7)', display:'flex', justifyContent:'center', alignItems:'center', zIndex:1000}}>
                <div className="modal-content" style={{backgroundColor:'#222', padding:'20px', borderRadius:'8px', width:'700px', maxHeight:'90vh', overflowY:'auto'}}>
                    <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'20px'}}>
                        <h2>Invite Codes: {selectedTournament.name}</h2>
                        <button onClick={() => setShowInviteCodesModal(false)} style={{background:'none', border:'none', color:'#ccc', fontSize:'20px', cursor:'pointer'}}>×</button>
                    </div>

                    <div style={{marginBottom:'20px', padding:'15px', backgroundColor:'#333', borderRadius:'4px'}}>
                        <h3>Generate Codes</h3>
                        <form onSubmit={generateInviteCodes} style={{display:'flex', gap:'10px', alignItems:'end'}}>
                            <div style={{flex:1}}>
                                <label style={{display:'block', marginBottom:'5px'}}>Number of codes</label>
                                <input 
                                    type="number" 
                                    min="1" 
                                    max="1000" 
                                    value={inviteCodeCount} 
                                    onChange={(e) => setInviteCodeCount(e.target.value)}
                                    style={{width:'100%', padding:'8px', backgroundColor:'#444', border:'1px solid #555', color:'white', borderRadius:'4px'}}
                                />
                            </div>
                            <button type="submit" disabled={loading} style={{padding:'8px 16px', backgroundColor:'#4CAF50', color:'white', border:'none', borderRadius:'4px', cursor:'pointer'}}>
                                {loading ? 'Generating...' : 'Generate'}
                            </button>
                        </form>
                    </div>

                    <div className="codes-list">
                        <h3>Existing Codes ({inviteCodes.length})</h3>
                        {inviteCodes.length === 0 ? (
                            <p style={{color:'#888'}}>No invite codes generated yet.</p>
                        ) : (
                            <div style={{maxHeight:'400px', overflowY:'auto'}}>
                                <table style={{width:'100%', borderCollapse:'collapse'}}>
                                    <thead>
                                        <tr style={{borderBottom:'1px solid #444', textAlign:'left'}}>
                                            <th style={{padding:'8px'}}>Code</th>
                                            <th style={{padding:'8px'}}>Status</th>
                                            <th style={{padding:'8px'}}>Used By</th>
                                            <th style={{padding:'8px'}}>Sent?</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {inviteCodes.map(code => (
                                            <tr key={code.id} style={{borderBottom:'1px solid #333', backgroundColor: code.is_used ? 'rgba(255,0,0,0.1)' : 'transparent'}}>
                                                <td style={{padding:'8px', fontFamily:'monospace', fontSize:'1.1em'}}>{code.code}</td>
                                                <td style={{padding:'8px'}}>
                                                    {code.is_used ? <span style={{color:'#f44336'}}>Used</span> : <span style={{color:'#4CAF50'}}>Available</span>}
                                                </td>
                                                <td style={{padding:'8px'}}>
                                                    {code.UsedByUser ? code.UsedByUser.username : '-'}
                                                </td>
                                                <td style={{padding:'8px'}}>
                                                    <label style={{display:'flex', alignItems:'center', cursor:'pointer'}}>
                                                        <input 
                                                            type="checkbox" 
                                                            checked={code.is_sent} 
                                                            onChange={() => handleToggleInviteCodeSent(code.id, code.is_sent)}
                                                            disabled={code.is_used}
                                                            style={{marginRight:'5px'}}
                                                        />
                                                        {code.is_sent ? 'Sent' : 'Not Sent'}
                                                    </label>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        )}
        {showCreateModal && (
            <div className="modal-overlay" style={{position:'fixed', top:0, left:0, right:0, bottom:0, backgroundColor:'rgba(0,0,0,0.7)', display:'flex', justifyContent:'center', alignItems:'center', zIndex:1000}}>
                <div className="modal-content" style={{backgroundColor:'#222', padding:'20px', borderRadius:'8px', width:'500px', maxHeight:'90vh', overflowY:'auto'}}>
                    <h2>Create Tournament</h2>
                    <form onSubmit={handleCreateTournament}>
                        <div className="form-group" style={{marginBottom:'15px'}}>
                            <label style={{display:'block', marginBottom:'5px'}}>Name</label>
                            <input type="text" required value={newTournament.name} onChange={e => setNewTournament({...newTournament, name: e.target.value})} style={{width:'100%', padding:'8px'}} />
                        </div>
                        <div className="form-group" style={{marginBottom:'15px'}}>
                            <label style={{display:'block', marginBottom:'5px'}}>Description</label>
                            <textarea value={newTournament.description} onChange={e => setNewTournament({...newTournament, description: e.target.value})} style={{width:'100%', padding:'8px'}} />
                        </div>
                        <div className="form-group" style={{marginBottom:'15px'}}>
                            <label style={{display:'block', marginBottom:'5px'}}>Type</label>
                            <select value={newTournament.type} onChange={e => setNewTournament({...newTournament, type: e.target.value})} style={{width:'100%', padding:'8px'}}>
                                <option value="swiss">Swiss</option>
                                <option value="arena">Arena</option>
                                <option value="bracket">Bracket</option>
                            </select>
                        </div>
                        <div className="form-group" style={{marginBottom:'15px'}}>
                             <label style={{display:'block', marginBottom:'5px'}}>Access Type</label>
                             <select value={newTournament.access_type} onChange={e => setNewTournament({...newTournament, access_type: e.target.value})} style={{width:'100%', padding:'8px'}}>
                                 <option value="public">Public</option>
                                 <option value="invite_only">Invite Only</option>
                             </select>
                        </div>
                        <div className="form-group" style={{marginBottom:'15px'}}>
                            <label style={{display:'block', marginBottom:'5px'}}>Max Players</label>
                            <input type="number" value={newTournament.max_players} onChange={e => setNewTournament({...newTournament, max_players: parseInt(e.target.value)})} style={{width:'100%', padding:'8px'}} />
                        </div>
                        <div className="form-group" style={{marginBottom:'15px'}}>
                            <label style={{display:'block', marginBottom:'5px'}}>Time Control (Mins / Inc)</label>
                            <div style={{display:'flex', gap:'10px'}}>
                                <input type="number" placeholder="Mins" value={newTournament.time_control.minutes} onChange={e => setNewTournament({...newTournament, time_control: {...newTournament.time_control, minutes: parseInt(e.target.value)}})} style={{width:'50%', padding:'8px'}} />
                                <input type="number" placeholder="Inc" value={newTournament.time_control.increment} onChange={e => setNewTournament({...newTournament, time_control: {...newTournament.time_control, increment: parseInt(e.target.value)}})} style={{width:'50%', padding:'8px'}} />
                            </div>
                        </div>

                        <div className="form-row" style={{display:'flex', gap:'10px', marginBottom:'15px'}}>
                           <div style={{flex:1}}>
                              <label style={{display:'block', marginBottom:'5px'}}>Start Time</label>
                              <input type="datetime-local" value={newTournament.start_time} onChange={e => setNewTournament({...newTournament, start_time: e.target.value})} style={{width:'100%', padding:'8px'}} />
                           </div>
                           <div style={{flex:1}}>
                              <label style={{display:'block', marginBottom:'5px'}}>End Time (Optional)</label>
                              <input type="datetime-local" value={newTournament.end_time} onChange={e => setNewTournament({...newTournament, end_time: e.target.value})} style={{width:'100%', padding:'8px'}} />
                           </div>
                        </div>

                        <div className="form-row" style={{display:'flex', gap:'10px', marginBottom:'15px'}}>
                           <div style={{flex:1}}>
                              <label style={{display:'block', marginBottom:'5px'}}>Reg. Start (Optional)</label>
                              <input type="datetime-local" value={newTournament.registration_start} onChange={e => setNewTournament({...newTournament, registration_start: e.target.value})} style={{width:'100%', padding:'8px'}} />
                           </div>
                           <div style={{flex:1}}>
                               <label style={{display:'block', marginBottom:'5px'}}>Reg. End (Optional)</label>
                               <input type="datetime-local" value={newTournament.registration_end} onChange={e => setNewTournament({...newTournament, registration_end: e.target.value})} style={{width:'100%', padding:'8px'}} />
                           </div>
                        </div>

                        <div className="form-group" style={{marginBottom:'15px'}}>
                            <label style={{display:'flex', alignItems:'center', cursor:'pointer'}}>
                                <input type="checkbox" checked={newTournament.is_money_prize} onChange={e => setNewTournament({...newTournament, is_money_prize: e.target.checked})} style={{marginRight:'10px'}} />
                                Has Money Prize / Entry Fee
                            </label>
                        </div>

                        {newTournament.is_money_prize && (
                            <div className="prize-pool-section" style={{padding:'10px', backgroundColor:'#333', borderRadius:'4px', marginBottom:'15px'}}>
                                <div className="form-group" style={{marginBottom:'10px'}}>
                                    <label style={{display:'block', marginBottom:'5px'}}>Currency</label>
                                    <input type="text" value={newTournament.prize_pool.currency} onChange={e => setNewTournament({...newTournament, prize_pool: {...newTournament.prize_pool, currency: e.target.value}})} style={{width:'100%', padding:'8px'}} />
                                </div>
                                <div className="form-row" style={{display:'flex', gap:'10px', marginBottom:'10px'}}>
                                    <div style={{flex:1}}>
                                        <label style={{display:'block', marginBottom:'5px'}}>Total Prize Pool</label>
                                        <input type="number" value={newTournament.prize_pool.total_amount} onChange={e => setNewTournament({...newTournament, prize_pool: {...newTournament.prize_pool, total_amount: parseFloat(e.target.value)}})} style={{width:'100%', padding:'8px'}} />
                                    </div>
                                    <div style={{flex:1}}>
                                         <label style={{display:'block', marginBottom:'5px'}}>Entry Fee</label>
                                         <input type="number" value={newTournament.prize_pool.entry_fee} onChange={e => setNewTournament({...newTournament, prize_pool: {...newTournament.prize_pool, entry_fee: parseFloat(e.target.value)}})} style={{width:'100%', padding:'8px'}} />
                                    </div>
                                </div>
                                <div className="form-group">
                                    <label style={{display:'block', marginBottom:'5px'}}>Distribution (%) - comma separated</label>
                                    <input type="text" placeholder="50, 30, 20" value={newTournament.prize_pool.distribution} onChange={e => setNewTournament({...newTournament, prize_pool: {...newTournament.prize_pool, distribution: e.target.value}})} style={{width:'100%', padding:'8px'}} />
                                </div>
                            </div>
                        )}
                        <div className="form-actions" style={{display:'flex', justifyContent:'flex-end', gap:'10px', marginTop:'20px'}}>
                            <button type="button" onClick={() => setShowCreateModal(false)} style={{padding:'8px 16px', cursor:'pointer'}}>Cancel</button>
                            <button type="submit" style={{padding:'8px 16px', backgroundColor:'#4CAF50', color:'white', border:'none', borderRadius:'4px', cursor:'pointer'}}>Create</button>
                        </div>
                    </form>
                </div>
            </div>
        )}

        {/* Invite Modal */}
        {showInviteModal && selectedTournament && (
            <div className="modal-overlay" style={{position:'fixed', top:0, left:0, right:0, bottom:0, backgroundColor:'rgba(0,0,0,0.7)', display:'flex', justifyContent:'center', alignItems:'center', zIndex:1000}}>
                <div className="modal-content" style={{backgroundColor:'#222', padding:'20px', borderRadius:'8px', width:'500px'}}>
                    <h2>Invite Users to {selectedTournament.name}</h2>
                    <div style={{marginBottom:'20px'}}>
                        <input 
                            type="text" 
                            placeholder="Search username..." 
                            value={inviteSearchQuery} 
                            onChange={e => setInviteSearchQuery(e.target.value)}
                            onKeyDown={e => e.key === 'Enter' && handleSearchUsers()}
                            style={{width:'70%', padding:'8px', marginRight:'10px'}}
                        />
                        <button onClick={handleSearchUsers} style={{padding:'8px 16px', cursor:'pointer'}}>Search</button>
                    </div>
                    <div className="search-results" style={{maxHeight:'300px', overflowY:'auto'}}>
                        {inviteSearchResults.map(user => (
                            <div key={user.id} style={{display:'flex', justifyContent:'space-between', alignItems:'center', padding:'10px', borderBottom:'1px solid #333'}}>
                                <span>{user.username} ({user.rating})</span>
                                <button onClick={() => handleInviteUser(user.id)} style={{cursor:'pointer', padding:'4px 8px', backgroundColor:'#2196F3', color:'white', border:'none', borderRadius:'4px'}}>Invite</button>
                            </div>
                        ))}
                    </div>
                    <div style={{marginTop:'20px', textAlign:'right'}}>
                        <button onClick={() => { setShowInviteModal(false); setInviteSearchResults([]); setInviteSearchQuery(''); }} style={{padding:'8px 16px', cursor:'pointer'}}>Close</button>
                    </div>
                </div>
            </div>
        )}

        {/* Edit Tournament Modal */}
        {showEditModal && selectedTournament && (
            <div className="modal-overlay" style={{position:'fixed', top:0, left:0, right:0, bottom:0, backgroundColor:'rgba(0,0,0,0.7)', display:'flex', justifyContent:'center', alignItems:'center', zIndex:1000}}>
                <div className="modal-content" style={{backgroundColor:'#222', padding:'20px', borderRadius:'8px', width:'500px', maxHeight:'90vh', overflowY:'auto'}}>
                    <h2>Edit Tournament: {selectedTournament.name}</h2>
                    <form onSubmit={handleUpdateTournament}>
                        <div className="form-group" style={{marginBottom:'15px'}}>
                            <label style={{display:'block', marginBottom:'5px'}}>Name</label>
                            <input type="text" value={editTournamentData.name} onChange={e => setEditTournamentData({...editTournamentData, name: e.target.value})} style={{width:'100%', padding:'8px'}} />
                        </div>
                        <div className="form-group" style={{marginBottom:'15px'}}>
                            <label style={{display:'block', marginBottom:'5px'}}>Description</label>
                            <textarea value={editTournamentData.description} onChange={e => setEditTournamentData({...editTournamentData, description: e.target.value})} style={{width:'100%', padding:'8px'}} />
                        </div>
                        <div className="form-group" style={{marginBottom:'15px'}}>
                            <label style={{display:'block', marginBottom:'5px'}}>Max Players</label>
                            <input type="number" value={editTournamentData.max_players} onChange={e => setEditTournamentData({...editTournamentData, max_players: parseInt(e.target.value)})} style={{width:'100%', padding:'8px'}} />
                        </div>

                        <div className="form-row" style={{display:'flex', gap:'10px', marginBottom:'15px'}}>
                           <div style={{flex:1}}>
                              <label style={{display:'block', marginBottom:'5px'}}>Start Time</label>
                              <input type="datetime-local" value={editTournamentData.start_time} onChange={e => setEditTournamentData({...editTournamentData, start_time: e.target.value})} style={{width:'100%', padding:'8px'}} />
                           </div>
                           <div style={{flex:1}}>
                              <label style={{display:'block', marginBottom:'5px'}}>End Time</label>
                              <input type="datetime-local" value={editTournamentData.end_time} onChange={e => setEditTournamentData({...editTournamentData, end_time: e.target.value})} style={{width:'100%', padding:'8px'}} />
                           </div>
                        </div>

                        <div className="form-row" style={{display:'flex', gap:'10px', marginBottom:'15px'}}>
                           <div style={{flex:1}}>
                              <label style={{display:'block', marginBottom:'5px'}}>Reg. Start</label>
                              <input type="datetime-local" value={editTournamentData.registration_start} onChange={e => setEditTournamentData({...editTournamentData, registration_start: e.target.value})} style={{width:'100%', padding:'8px'}} />
                           </div>
                           <div style={{flex:1}}>
                               <label style={{display:'block', marginBottom:'5px'}}>Reg. End</label>
                               <input type="datetime-local" value={editTournamentData.registration_end} onChange={e => setEditTournamentData({...editTournamentData, registration_end: e.target.value})} style={{width:'100%', padding:'8px'}} />
                           </div>
                        </div>

                        <div className="form-group" style={{marginBottom:'15px'}}>
                            <label style={{display:'flex', alignItems:'center', cursor:'pointer'}}>
                                <input type="checkbox" checked={editTournamentData.is_money_prize} onChange={e => setEditTournamentData({...editTournamentData, is_money_prize: e.target.checked})} style={{marginRight:'10px'}} />
                                Has Money Prize / Entry Fee
                            </label>
                        </div>

                        {editTournamentData.is_money_prize && (
                            <div className="prize-pool-section" style={{padding:'10px', backgroundColor:'#333', borderRadius:'4px', marginBottom:'15px'}}>
                                <div className="form-group" style={{marginBottom:'10px'}}>
                                    <label style={{display:'block', marginBottom:'5px'}}>Currency</label>
                                    <input type="text" value={editTournamentData.prize_pool.currency} onChange={e => setEditTournamentData({...editTournamentData, prize_pool: {...editTournamentData.prize_pool, currency: e.target.value}})} style={{width:'100%', padding:'8px'}} />
                                </div>
                                <div className="form-row" style={{display:'flex', gap:'10px', marginBottom:'10px'}}>
                                    <div style={{flex:1}}>
                                        <label style={{display:'block', marginBottom:'5px'}}>Total Prize Pool</label>
                                        <input type="number" value={editTournamentData.prize_pool.total_amount} onChange={e => setEditTournamentData({...editTournamentData, prize_pool: {...editTournamentData.prize_pool, total_amount: parseFloat(e.target.value)}})} style={{width:'100%', padding:'8px'}} />
                                    </div>
                                    <div style={{flex:1}}>
                                         <label style={{display:'block', marginBottom:'5px'}}>Entry Fee</label>
                                         <input type="number" value={editTournamentData.prize_pool.entry_fee} onChange={e => setEditTournamentData({...editTournamentData, prize_pool: {...editTournamentData.prize_pool, entry_fee: parseFloat(e.target.value)}})} style={{width:'100%', padding:'8px'}} />
                                    </div>
                                </div>
                                <div className="form-group">
                                    <label style={{display:'block', marginBottom:'5px'}}>Distribution (%) - comma separated</label>
                                    <input type="text" placeholder="50, 30, 20" value={editTournamentData.prize_pool.distribution} onChange={e => setEditTournamentData({...editTournamentData, prize_pool: {...editTournamentData.prize_pool, distribution: e.target.value}})} style={{width:'100%', padding:'8px'}} />
                                </div>
                            </div>
                        )}
                        <div className="form-actions" style={{display:'flex', justifyContent:'flex-end', gap:'10px'}}>
                            <button type="button" onClick={() => setShowEditModal(false)} style={{padding:'8px 16px', cursor:'pointer'}}>Cancel</button>
                            <button type="submit" style={{padding:'8px 16px', backgroundColor:'#2196F3', color:'white', border:'none', borderRadius:'4px', cursor:'pointer'}}>Update</button>
                        </div>
                    </form>
                </div>
            </div>
        )}

        {/* Invite Codes Modal */}
        {showInviteCodesModal && selectedTournament && (
            <div className="modal-overlay" style={{position:'fixed', top:0, left:0, right:0, bottom:0, backgroundColor:'rgba(0,0,0,0.7)', display:'flex', justifyContent:'center', alignItems:'center', zIndex:1000}}>
                <div className="modal-content" style={{backgroundColor:'#222', padding:'20px', borderRadius:'8px', width:'600px', maxHeight:'90vh', overflowY:'auto'}}>
                    <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'20px'}}>
                        <h2>Invite Codes: {selectedTournament.name}</h2>
                        <button onClick={() => setShowInviteCodesModal(false)} style={{cursor:'pointer', background:'none', border:'none', color:'#aaa', fontSize:'20px'}}>×</button>
                    </div>
                    
                    <div className="codes-actions" style={{display:'flex', gap:'10px', marginBottom:'20px', alignItems:'flex-end'}}>
                        <div style={{flex:1}}>
                           <label style={{display:'block', marginBottom:'5px'}}>Generate Count</label>
                           <input type="number" min="1" max="100" value={inviteCodeCount} onChange={e => setInviteCodeCount(e.target.value)} style={{padding:'8px', width:'100%'}} />
                        </div>
                        <button onClick={generateInviteCodes} disabled={loading} style={{padding:'8px 16px', backgroundColor:'#2196F3', color:'white', border:'none', borderRadius:'4px', cursor:'pointer', height:'35px'}}>Generate</button>
                        <button onClick={exportInviteCodes} disabled={!inviteCodes.length} style={{padding:'8px 16px', backgroundColor:'#4CAF50', color:'white', border:'none', borderRadius:'4px', cursor:'pointer', height:'35px'}}>Export CSV</button>
                    </div>

                    <div className="codes-list" style={{maxHeight:'400px', overflowY:'auto', border:'1px solid #444', borderRadius:'4px'}}>
                        <table style={{width:'100%', borderCollapse:'collapse'}}>
                           <thead>
                              <tr style={{backgroundColor:'#333', textAlign:'left'}}>
                                  <th style={{padding:'10px'}}>Code</th>
                                  <th style={{padding:'10px'}}>Status</th>
                                  <th style={{padding:'10px'}}>Used By</th>
                                  <th style={{padding:'10px'}}>Created</th>
                              </tr>
                           </thead>
                           <tbody>
                              {inviteCodes.length > 0 ? (
                                  inviteCodes.map(code => (
                                      <tr key={code.id} style={{borderBottom:'1px solid #333'}}>
                                          <td style={{padding:'10px', fontFamily:'monospace', fontSize:'1.1em'}}>{code.code}</td>
                                          <td style={{padding:'10px'}}>
                                              <span style={{
                                                  padding:'2px 6px', 
                                                  borderRadius:'4px', 
                                                  fontSize:'0.8em',
                                                  backgroundColor: code.is_used ? '#f44336' : '#4CAF50'
                                              }}>
                                                  {code.is_used ? 'USED' : 'ACTIVE'}
                                              </span>
                                          </td>
                                          <td style={{padding:'10px'}}>{code.used_by_user_id ? code.used_by_user_id.substring(0,8)+'...' : '-'}</td>
                                          <td style={{padding:'10px'}}>{new Date(code.created_at).toLocaleDateString()}</td>
                                      </tr>
                                  ))
                              ) : (
                                  <tr>
                                      <td colSpan="4" style={{padding:'20px', textAlign:'center', color:'#888'}}>No invite codes generated yet</td>
                                  </tr>
                              )}
                           </tbody>
                        </table>
                    </div>
                </div>
            </div>
        )}

    </div>
  );
};

export default AdminPage;